import { useEffect, useState } from 'react';
import axios from 'axios';

import type { IssueTokenResponse } from '~/components/AiChat/types';

const TOKEN_RETRY_DELAY_MS = 2000;
const TOKEN_MAX_ATTEMPTS = 5;
const HTTP_NOT_FOUND = 404;

/**
 * Fetches a real-time (Firestore) token from a CMS `.../token` route; `null` until one is issued.
 *
 * A null token or a transient error is retried briefly (the AI task id can commit after the row is
 * shown). A 404 is final: the route answers the same on every attempt, e.g. a feature flag that is off.
 * Pass a `null` url to fetch nothing. The token is dropped the moment the url changes, so a stale
 * subscription never shows against a new row or claim.
 */
export const useRealTimeToken = (url: string | null): IssueTokenResponse | null => {
  const [issueTokenResponse, setIssueTokenResponse] = useState<IssueTokenResponse | null>(null);

  useEffect(() => {
    setIssueTokenResponse(null);
    if (!url) return undefined;

    let aborted = false;
    let retryTimeout: ReturnType<typeof setTimeout>;

    const fetchToken = async (attempt: number): Promise<void> => {
      try {
        const { data } = await axios.get<{ issue_token_response: IssueTokenResponse | null }>(url);
        if (aborted) return;
        if (data.issue_token_response) {
          setIssueTokenResponse(data.issue_token_response);
        } else if (attempt < TOKEN_MAX_ATTEMPTS) {
          retryTimeout = setTimeout(() => fetchToken(attempt + 1), TOKEN_RETRY_DELAY_MS);
        }
      } catch (error) {
        if (aborted || (axios.isAxiosError(error) && error.response?.status === HTTP_NOT_FOUND)) return;
        if (attempt < TOKEN_MAX_ATTEMPTS) {
          retryTimeout = setTimeout(() => fetchToken(attempt + 1), TOKEN_RETRY_DELAY_MS);
        }
      }
    };

    fetchToken(1);

    return () => {
      aborted = true;
      clearTimeout(retryTimeout);
    };
  }, [url]);

  return issueTokenResponse;
};

export default useRealTimeToken;
